import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Zap, CheckCircle, Clock, RefreshCw, Plus, Pencil, Trash2, X, Play, Pause } from "lucide-react";
import Header from "../components/layout/Header";
import { SearchInput } from "../components/ui/index";
import { automatisationsAPI, cascadeAPI, projetsAPI } from "../api/services";
import { useAuth } from "../context/AuthContext";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";
import { buildAutomatisationPreview } from "../utils/naturalLanguage";

export default function Automatisations() {
  const { t } = useTranslation();
  const { user, canDo, adminMode } = useAuth();
  const isMembre = user?.role === 'membre';
  const navigate = useNavigate();
  const [revendeur, setRevendeur] = useState("");
  const [client, setClient] = useState("");
  const [projet, setProjet] = useState("");
  const [search, setSearch] = useState("");

  // Data state
  const [automatisations, setAutomatisations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cascade filter state
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);

  const [deleteItem, setDeleteItem] = useState(null);

  const filtersSelected = !!projet;

  // Fetch automatisations
  const fetchAutomatisations = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (revendeur) params.revendeur = revendeur;
      if (client) params.client = client;
      if (projet) params.projet = projet;

      const { data } = await automatisationsAPI.getAll(params);
      const list = data.data || data;
      setAutomatisations(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Erreur chargement automatisations:", err);
    } finally {
      setLoading(false);
    }
  }, [search, revendeur, client, projet]);

  useEffect(() => { fetchAutomatisations(); }, [fetchAutomatisations]);

  // Membre : charger ses projets IoT (directs + via chantiers)
  useEffect(() => {
    if (!isMembre) return;
    projetsAPI.mesProjetsIoT().then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isMembre]);

  // Admin/Revendeur : cascade classique
  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.revendeurs().then(res => {
      setRevendeurs(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isMembre]);

  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.clients(revendeur || undefined).then(res => {
      setClients(res.data.data || res.data || []);
    }).catch(console.error);
  }, [revendeur, isMembre]);

  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.projets(client || undefined, revendeur || undefined).then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [client, revendeur, isMembre]);

  const handleRevendeurChange = (v) => { setRevendeur(v); setClient(""); setProjet(""); };
  const handleClientChange = (v) => { setClient(v); setProjet(""); };
  const resetFilters = () => { setRevendeur(""); setClient(""); setProjet(""); setSearch(""); };

  const handleDelete = async () => {
    try {
      await automatisationsAPI.remove(deleteItem._id);
      setDeleteItem(null);
      fetchAutomatisations();
    } catch (err) {
      console.error("Erreur suppression automatisation:", err);
    }
  };

  const handleRestart = async (id) => {
    try {
      await automatisationsAPI.restart(id);
      fetchAutomatisations();
    } catch (err) {
      console.error("Erreur redémarrage:", err);
    }
  };

  const handlePause = async (id) => {
    try {
      await automatisationsAPI.toggle(id);
      fetchAutomatisations();
    } catch (err) {
      console.error("Erreur mise en pause:", err);
    }
  };

  const total = automatisations.length;
  const actives = automatisations.filter(a => a.statut === "Actif").length;
  const enPause = automatisations.filter(a => a.statut === "En pause").length;
  const totalExecutions = automatisations.reduce((s, a) => s + (a.executions || 0), 0);

  return (
    <>
      <Header title={t('automations.title')} subtitle={t('automations.subtitle')} />

      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
          <Zap size={16} className="text-purple-600" />
        </div>
        <h2 className="font-semibold text-slate-800">{t('automations.title')}</h2>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        {adminMode === 'platform' && (
          <select value={revendeur} onChange={e => handleRevendeurChange(e.target.value)}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">{t('common.allResellers')} ({revendeurs.length})</option>
            {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
          </select>
        )}
        {!isMembre && (
          <select value={client} onChange={e => handleClientChange(e.target.value)}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">{t('common.allClients')} ({clients.length})</option>
            {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
          </select>
        )}
        <select value={projet} onChange={e => setProjet(e.target.value)}
          className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">{t('common.allProjects')} ({projets.length})</option>
          {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
        </select>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1">
          <SearchInput value={search} onChange={setSearch} />
        </div>
        {(revendeur || client || projet || search) && (
          <button onClick={resetFilters}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
            <X size={14} /> {t('automations.reset')}
          </button>
        )}
      </div>

      {/* Stats + Bouton créer */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        {[
          { icon: Zap, bg: "bg-purple-100", color: "text-purple-600", label: t('automations.totalAutomations'), value: total },
          { icon: CheckCircle, bg: "bg-green-100", color: "text-green-600", label: t('automations.active'), value: actives },
          { icon: Clock, bg: "bg-yellow-100", color: "text-yellow-600", label: t('automations.paused'), value: enPause },
          { icon: RefreshCw, bg: "bg-pink-100", color: "text-pink-600", label: t('automations.executions'), value: totalExecutions },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.bg}`}>
              <s.icon size={22} className={s.color} />
            </div>
            <div>
              <p className="text-slate-500 text-xs">{s.label}</p>
              <p className="text-2xl font-bold text-slate-800">{s.value}</p>
            </div>
          </div>
        ))}

        {canDo('configIoT') && (
          <button
            onClick={() => filtersSelected && navigate(`/automatisations/creer?projet=${projet}&client=${client}&revendeur=${revendeur}`)}
            className={`rounded-2xl p-5 flex items-center justify-center gap-2 font-medium text-sm transition-all ${
              filtersSelected
                ? "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer shadow-sm"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Plus size={18} /> {t('automations.create')}
          </button>
        )}
      </div>

      {/* Liste ou chargement */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <RefreshCw size={32} className="text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-slate-400">{t('automations.loading')}</p>
        </div>
      ) : automatisations.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-dashed border-gray-200">
          <Zap size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="text-slate-400 font-medium">{t('automations.noAutomations')}</p>
          <p className="text-slate-300 text-sm">{t('automations.createFirst')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {automatisations.map((a) => (
            <div key={a._id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Zap size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">{a.nom}</h3>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="text-xs bg-gray-100 text-slate-600 px-2 py-1 rounded-full">🏢 {a.revendeur?.nom}</span>
                      <span className="text-xs bg-gray-100 text-slate-600 px-2 py-1 rounded-full">👤 {a.client?.nom}</span>
                      <span className="text-xs bg-gray-100 text-slate-600 px-2 py-1 rounded-full">📁 {a.projet?.nom}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        a.statut === 'Actif' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>{a.statut === 'Actif' ? '✅' : '⏸️'} {a.statut}</span>
                    </div>
                    <p className="text-slate-500 text-sm italic mb-3">"{a.description}"</p>
                    {/* Aperçu langage naturel */}
                    {(() => {
                      const preview = buildAutomatisationPreview(a);
                      if (!preview) return null;
                      return (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[9px] bg-teal-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">Langage naturel</span>
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed">
                            <span className="font-semibold text-teal-700">SI </span>
                            <span>on est le {preview.joursText}</span>
                            {preview.moisText && <span> en {preview.moisText}</span>}
                          </p>
                          <p className="text-xs text-slate-700 leading-relaxed mt-0.5">
                            <span className="font-semibold text-orange-600">ALORS </span>
                            <span>{preview.creneauxText.join(", puis ")}</span>
                          </p>
                        </div>
                      );
                    })()}
                    {/* Actionneurs */}
                    {Array.isArray(a.actionneurs) && a.actionneurs.length > 0 && (
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {a.actionneurs.map(act => (
                          <span key={act._id || act} className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-1 rounded-full">
                            ⚡ {act.sn || act._id || act}{act.type ? ` - ${act.type}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Créneaux détaillés */}
                    {Array.isArray(a.creneaux) && a.creneaux.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {a.creneaux.map((c, idx) => {
                          const cActNames = (c.actionneurs && c.actionneurs.length > 0)
                            ? c.actionneurs.map(id => {
                                const act = (a.actionneurs || []).find(x => (x._id || x) === id);
                                return act?.sn || (typeof id === 'string' ? id.slice(-6) : id);
                              }).join(", ")
                            : null;
                          return (
                            <div key={idx} className="flex items-center gap-1.5 text-xs bg-blue-50 border border-blue-200 px-2.5 py-1.5 rounded-lg">
                              <Clock size={11} className="text-blue-500" />
                              <span className="font-medium text-blue-700">{c.debut} → {c.fin}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.action === 'Activer' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {c.action === 'Activer' ? 'ON' : 'OFF'}
                              </span>
                              {cActNames && (
                                <span className="text-orange-600 font-medium">→ {cActNames}</span>
                              )}
                              {!cActNames && (a.actionneurs || []).length > 1 && (
                                <span className="text-slate-400">→ Tous</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="text-blue-600">📅 {a.frequence}</span>
                      <span>🔄 {a.executions || 0} {t('automations.executionsLabel')}</span>
                      <span>⏱ {t('automations.lastExecution')} {a.derniere || "—"}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.statut === 'En pause' && (
                    <button
                      onClick={() => handleRestart(a._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-all">
                      <Play size={15} /> Redémarrer
                    </button>
                  )}
                  {a.statut === 'Actif' && (
                    <button
                      onClick={() => handlePause(a._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 transition-all">
                      <Pause size={15} /> Mettre en pause
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/automatisations/edit/${a._id}`)}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all">
                    <Pencil size={15} /> {t('automations.view')}
                  </button>
                  <button
                    onClick={() => setDeleteItem(a)}
                    className="p-2 border border-gray-200 rounded-xl text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={handleDelete}
        itemName={deleteItem?.nom}
        itemType="automatisation"
      />
    </>
  );
}
